'use client';
import React, { useState, useEffect, useRef } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: 'What is Talent?',
    answer: 'Talent is the #1 marketplace connecting TikTok Shop live stream sellers with brands and businesses looking to scale their live commerce. Sellers create verified profiles with real GMV data, and brands post job listings or browse our seller directory to find the perfect match.',
  },
  {
    question: 'How does the matching work?',
    answer: 'Our AI-powered matching engine analyzes seller performance data (GMV, engagement rate, niche, audience demographics) alongside brand requirements (product category, target audience, budget, campaign goals) to surface the most compatible matches. You can also browse and filter manually.',
  },
  {
    question: 'How are seller profiles verified?',
    answer: 'Every seller on Talent connects their TikTok Shop account via our secure API integration. We pull real-time data including GMV history, follower count, engagement rates, and stream frequency. No self-reported numbers — everything is verified directly from TikTok.',
  },
  {
    question: 'How does payment and escrow work?',
    answer: 'Brands fund an escrow account before a collaboration begins. Funds are held securely and released to the seller upon completion of agreed milestones (e.g., number of streams, GMV targets). Both parties are protected throughout the entire collaboration.',
  },
  {
    question: 'Can I try Talent for free?',
    answer: 'Yes! All plans include a 14-day free trial with no credit card required. Sellers can create a free profile and apply to jobs. Brands can post one job listing and browse seller profiles for free before upgrading to a paid plan.',
  },
  {
    question: 'What niches and categories are supported?',
    answer: 'Talent supports all TikTok Shop categories including Beauty & Skincare, Fashion & Apparel, Health & Wellness, Tech & Electronics, Home & Living, Food & Beverage, Sports & Fitness, and more. New categories are added regularly based on platform demand.',
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const left = entry.target.querySelector('.faq-left');
            const right = entry.target.querySelector('.faq-right');
            if (left) {
              (left as HTMLElement).style.opacity = '1';
              (left as HTMLElement).style.transform = 'translateY(0)';
            }
            if (right) {
              setTimeout(() => {
                (right as HTMLElement).style.opacity = '1';
                (right as HTMLElement).style.transform = 'translateY(0)';
              }, 200);
            }
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-24 bg-white" ref={sectionRef}>
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left */}
          <div
            className="faq-left"
            style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
          >
            <div className="mb-6">
              <div
                className="bg-theme-04 text-dark text-[14px] font-semibold px-4 py-1.5 rounded-2xl inline-block"
                style={{ transform: 'rotate(10deg)' }}
              >
                FAQ
              </div>
            </div>
            <h2 className="text-[40px] md:text-[48px] font-bold text-dark leading-tight mb-5">
              Frequently Asked Questions
            </h2>
            <p className="text-[17px] text-dark/60 leading-relaxed mb-8">
              Have more questions? Reach out to our support team and we&apos;ll get back to you within 24 hours.
            </p>
            <a
              href="#"
              className="inline-flex items-center gap-2 font-bold text-[15px] text-dark border border-dark/20 px-7 py-3.5 rounded-pill hover:bg-neutral-01 transition-all duration-200"
            >
              Contact Support
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          </div>

          {/* Right - FAQ accordion */}
          <div
            className="faq-right"
            style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
          >
            <div className="space-y-0">
              {faqs.map((faq, i) => (
                <div
                  key={i}
                  className="border-b border-neutral-02 last:border-0"
                >
                  <button
                    className="w-full flex items-center justify-between py-6 text-left group"
                    onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  >
                    <p className="text-[16px] font-semibold text-dark pr-4 group-hover:text-theme-01 transition-colors duration-200">
                      {faq.question}
                    </p>
                    <div
                      className={`w-8 h-8 rounded-full border border-dark/20 flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                        openIndex === i ? 'bg-dark border-dark rotate-45' : 'bg-white'
                      }`}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        className={openIndex === i ? 'text-white' : 'text-dark'}
                      >
                        <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                      </svg>
                    </div>
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openIndex === i ? 'max-h-48 pb-6' : 'max-h-0'
                    }`}
                  >
                    <p className="text-[15px] text-dark/60 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
